import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Button, message, DatePicker } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { AppDispatch, RootState } from '../../../store/store';
import { icTypeOptions, SME_ORGANIZATION_TYPE } from '../../../enum/smeApplication';
import { getRelationshipListThunk } from '../../../services/dropdownParameterService/dropdownParameterThunk';
import { getCityStateByPostcodeThunk } from '../../../services/locationService/locationThunk';
import { phoneRegexPattern, emailRegexPattern } from '../../../utils/validation';
import { updateAccountDetailsSmeApplicationThunk } from '../../../services/collectionService/collectionThunk';
import { getAccountDetailsThunk } from '../../../services/collectionService/collectionThunk';
import { getNricInfo } from '../../../utils/nric.util';

interface EditSmeCustomerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EditSmeCustomerDetailsModal: React.FC<EditSmeCustomerDetailsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  const { accountEnquiry } = useSelector((state: RootState) => state.collection);

  const { relationshipState } = useSelector((state: RootState) => state.dropdownParameter);

  const relationshipOptions = relationshipState?.data.map((item) => ({
    label: item.name,
    value: item.name,
  }));

  const data = accountEnquiry?.data?.accountDetails?.smeApplication;
  const organizationType = accountEnquiry?.data?.accountDetails?.organizationType;
  const accountDetailsId = accountEnquiry?.data?.accountDetails?.id;

  useEffect(() => {
    if (data && isOpen) {
      form.setFieldsValue({
        // Disabled fields
        name: data?.name,
        nationality: data?.nationality,
        applicantRace: data?.applicantRace,
        nric: data?.nric,
        dateOfBirth: data?.dateOfBirth ? dayjs(data?.dateOfBirth) : null,
        age: data?.nric ? getNricInfo(data?.nric).age : null,
        occupationType: data?.occupationType,
        nricAddress1: data?.nricAddress1,
        nricAddress2: data?.nricAddress2,
        nricAddress3: data?.nricAddress3,
        nricPostcode: data?.nricPostcode,
        nricCity: data?.nricCity,
        nricState: data?.nricState,
        employerName: data?.employerName,
        applicantEmploymentType: data?.applicantEmploymentType,

        // Editable fields
        icType: data?.icType,
        emailAddress: data?.emailAddress,
        applicantPhoneNo: data?.applicantPhoneNo,
        homeAddress1: data?.homeAddress1,
        homeAddress2: data?.homeAddress2,
        homeAddress3: data?.homeAddress3,
        homePostcode: data?.homePostcode,
        homeCity: data?.homeCity,
        homeState: data?.homeState,
        mailingAddress1: data?.mailingAddress1,
        mailingAddress2: data?.mailingAddress2,
        mailingAddress3: data?.mailingAddress3,
        mailingPostcode: data?.mailingPostcode,
        mailingCity: data?.mailingCity,
        mailingState: data?.mailingState,
        emergencyContactPersonName: data?.emergencyContactPersonName,
        emergencyContactPersonPhoneNo: data?.emergencyContactPersonPhoneNo,
        emergencyContactEmail: data?.emergencyContactEmail,
        emergencyContactRelationship: data?.emergencyContactRelationship,
      });
    }
  }, [data, isOpen, form]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getRelationshipListThunk());
    }
  }, [isOpen, dispatch]);

  const handleNricPostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const postcode = e.target.value;
    if (postcode.length === 5 && /^\d+$/.test(postcode)) {
      dispatch(getCityStateByPostcodeThunk({ postcode }))
        .unwrap()
        .then((res) => {
          form.setFieldsValue({
            nricCity: res[0].post_office,
            nricState: res[0].state_code,
          });
        })
        .catch(() => {
          form.setFieldsValue({
            nricCity: null,
            nricState: null,
          });
        });
    } else {
      form.setFieldsValue({
        nricCity: null,
        nricState: null,
      });
    }
  };

  const handleHomePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const postcode = e.target.value;
    if (postcode.length === 5 && /^\d+$/.test(postcode)) {
      dispatch(getCityStateByPostcodeThunk({ postcode }))
        .unwrap()
        .then((res) => {
          form.setFieldsValue({
            homeCity: res[0].post_office,
            homeState: res[0].state_code,
          });
        })
        .catch(() => {
          form.setFieldsValue({
            homeCity: null,
            homeState: null,
          });
        });
    } else {
      form.setFieldsValue({
        homeCity: null,
        homeState: null,
      });
    }
  };

  const handleMailingPostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const postcode = e.target.value;
    if (postcode.length === 5 && /^\d+$/.test(postcode)) {
      dispatch(getCityStateByPostcodeThunk({ postcode }))
        .unwrap()
        .then((res) => {
          form.setFieldsValue({
            mailingCity: res[0].post_office,
            mailingState: res[0].state_code,
          });
        })
        .catch(() => {
          form.setFieldsValue({
            mailingCity: null,
            mailingState: null,
          });
        });
    } else {
      form.setFieldsValue({
        mailingCity: null,
        mailingState: null,
      });
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);

      // Only send editable fields
      const editableFields = {
        icType: values.icType,
        emailAddress: values.emailAddress,
        applicantPhoneNo: values.applicantPhoneNo,
        nricAddress1: values.nricAddress1,
        nricAddress2: values.nricAddress2,
        nricAddress3: values.nricAddress3,
        nricPostcode: values.nricPostcode,
        nricCity: values.nricCity,
        nricState: values.nricState,
        homeAddress1: values.homeAddress1,
        homeAddress2: values.homeAddress2,
        homeAddress3: values.homeAddress3,
        homePostcode: values.homePostcode,
        homeCity: values.homeCity,
        homeState: values.homeState,
        mailingAddress1: values.mailingAddress1,
        mailingAddress2: values.mailingAddress2,
        mailingAddress3: values.mailingAddress3,
        mailingPostcode: values.mailingPostcode,
        mailingCity: values.mailingCity,
        mailingState: values.mailingState,
      };

      // Add emergency fields if applicable
      if (organizationType !== SME_ORGANIZATION_TYPE.COMPANY) {
        Object.assign(editableFields, {
          emergencyContactPersonName: values.emergencyContactPersonName,
          emergencyContactPersonPhoneNo: values.emergencyContactPersonPhoneNo,
          emergencyContactEmail: values.emergencyContactEmail,
          emergencyContactRelationship: values.emergencyContactRelationship,
        });
      }

      await dispatch(
        updateAccountDetailsSmeApplicationThunk({
          id: accountDetailsId,
          payload: Object.keys(editableFields).reduce((acc, key) => {
            // Don't include null values
            const value = editableFields[key as keyof typeof editableFields];
            if (value !== null) {
              acc[key as keyof typeof editableFields] = value;
            }
            return acc;
          }, {} as typeof editableFields),
        })
      ).unwrap();

      // Refresh account details
      await dispatch(
        getAccountDetailsThunk({
          agreementNo: accountEnquiry?.data?.accountDetails?.agreementNo,
        })
      ).unwrap();

      onClose();
      form.resetFields();
      message.success('Updated successfully');
    } catch (error: any) {
      message.error(error?.message || 'Failed to update');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title="Edit SME Customer Details"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={() => form.submit()}>
          Save
        </Button>,
      ]}
      width={1200}
      className="overflow-auto"
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">
        {/* Customer Information Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Customer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item name="name" label="Full Name">
              <Input disabled />
            </Form.Item>
            <Form.Item name="nationality" label="Nationality">
              <Input disabled />
            </Form.Item>
            <Form.Item name="applicantRace" label="Race">
              <Input disabled />
            </Form.Item>
          </div>
        </div>

        {/* Identity Information Section */}
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="icType"
              label="Identity Card Type"
              rules={[{ required: true, message: 'Identity Card Type is Required' }]}
            >
              <Select placeholder={'Please Select'} options={icTypeOptions} />
            </Form.Item>
            <Form.Item name="nric" label="NRIC">
              <Input disabled />
            </Form.Item>
            <Form.Item name="dateOfBirth" label="Date Of Birth">
              <DatePicker className="w-full" disabled />
            </Form.Item>
            <Form.Item name="age" label="Age">
              <Input disabled />
            </Form.Item>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="emailAddress"
              label="Email Address"
              rules={[
                { required: true, message: 'Email Address is Required' },
                { pattern: emailRegexPattern, message: 'Email is Invalid' },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="applicantPhoneNo"
              label="Contact No."
              rules={[
                { required: true, message: 'Contact No is Required' },
                { pattern: phoneRegexPattern, message: 'Invalid Phone No' },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="occupationType" label="Occupation Type">
              <Input disabled />
            </Form.Item>
          </div>
        </div>

        {/* NRIC Address */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">NRIC Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="nricAddress1"
              label="Address Line 1"
              rules={[{ required: true, message: 'NRIC Address Line 1 is Required' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="nricAddress2" label="Address Line 2">
              <Input />
            </Form.Item>
            <Form.Item name="nricAddress3" label="Address Line 3">
              <Input />
            </Form.Item>
            <Form.Item
              name="nricPostcode"
              label="Postcode"
              rules={[
                { required: true, message: 'Postcode is Required' },
                { pattern: /^\d{5}$/, message: 'Postcode must be 5 digits' },
              ]}
            >
              <Input onChange={handleNricPostcodeChange} maxLength={5} />
            </Form.Item>
            <Form.Item
              name="nricCity"
              label="City"
              rules={[{ required: true, message: 'City is Required' }]}
            >
              <Input disabled={true} />
            </Form.Item>
            <Form.Item
              name="nricState"
              label="State"
              rules={[{ required: true, message: 'State is Required' }]}
            >
              <Input disabled={true} />
            </Form.Item>
          </div>
        </div>

        {/* Home Address */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Home Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="homeAddress1"
              label="Address Line 1"
              rules={[{ required: true, message: 'Home Address Line 1 is Required' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="homeAddress2" label="Address Line 2">
              <Input />
            </Form.Item>
            <Form.Item name="homeAddress3" label="Address Line 3">
              <Input />
            </Form.Item>
            <Form.Item
              name="homePostcode"
              label="Postcode"
              rules={[
                { required: true, message: 'Postcode is Required' },
                { pattern: /^\d{5}$/, message: 'Postcode must be 5 digits' },
              ]}
            >
              <Input onChange={handleHomePostcodeChange} maxLength={5} />
            </Form.Item>
            <Form.Item
              name="homeCity"
              label="City"
              rules={[{ required: true, message: 'City is Required' }]}
            >
              <Input disabled={true} />
            </Form.Item>
            <Form.Item
              name="homeState"
              label="State"
              rules={[{ required: true, message: 'State is Required' }]}
            >
              <Input disabled={true} />
            </Form.Item>
          </div>
        </div>

        {/* Mailing Address */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Mailing Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="mailingAddress1"
              label="Address Line 1"
              rules={[{ required: true, message: 'Mailing Address Line 1 is Required' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="mailingAddress2" label="Address Line 2">
              <Input />
            </Form.Item>
            <Form.Item name="mailingAddress3" label="Address Line 3">
              <Input />
            </Form.Item>
            <Form.Item
              name="mailingPostcode"
              label="Postcode"
              rules={[
                { required: true, message: 'Postcode is Required' },
                { pattern: /^\d{5}$/, message: 'Postcode must be 5 digits' },
              ]}
            >
              <Input onChange={handleMailingPostcodeChange} maxLength={5} />
            </Form.Item>
            <Form.Item
              name="mailingCity"
              label="City"
              rules={[{ required: true, message: 'City is Required' }]}
            >
              <Input disabled={true} />
            </Form.Item>
            <Form.Item
              name="mailingState"
              label="State"
              rules={[{ required: true, message: 'State is Required' }]}
            >
              <Input disabled={true} />
            </Form.Item>
          </div>
        </div>

        {/* Employment Information */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Employment Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {organizationType === SME_ORGANIZATION_TYPE.INDIVIDUAL && (
              <Form.Item name="applicantEmploymentType" label="Employment Type">
                <Input disabled />
              </Form.Item>
            )}
            <Form.Item name="employerName" label="Name of Employer / Nature of Business">
              <Input disabled />
            </Form.Item>
          </div>
        </div>

        {/* Emergency Information - Only show for non-COMPANY organization types */}
        {organizationType !== SME_ORGANIZATION_TYPE.COMPANY && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Emergency Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="emergencyContactPersonName"
                label="Emergency Name"
                rules={[{ required: true, message: 'Emergency Name is Required' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="emergencyContactPersonPhoneNo"
                label="Emergency Contact Number"
                rules={[
                  { required: true, message: 'Emergency Contact Number is Required' },
                  { pattern: phoneRegexPattern, message: 'Invalid Phone No' },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="emergencyContactEmail"
                label="Emergency Email"
                rules={[
                  { required: true, message: 'Emergency Email is Required' },
                  { pattern: emailRegexPattern, message: 'Email is Invalid' },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="emergencyContactRelationship"
                label="Relationship"
                rules={[{ required: true, message: 'Relationship is Required' }]}
              >
                <Select loading={relationshipState.isLoading} options={relationshipOptions} />
              </Form.Item>
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
};
